<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import BaseComment from '@/components/base/BaseComment.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import SummaryTableWrapper from '@/components/ConsultationSummary/SummaryTableWrapper.vue';

import type { Pagination, SummaryFormCommentDto } from '@/services/types/api.types';
import { useSummariesStore } from '@/stores/summaries.store';

// PROPS
const props = defineProps<{
  formId: number;
  areCommentsEnabled: boolean;
}>();

// CONST
const { t } = useI18n();

// STORE
const summariesStore = useSummariesStore();
const { formComments } = storeToRefs(summariesStore);

// REFS
const columns = computed(() => [
  {
    name: 'comment',
    label: t('form.summary.results.comments'),
    align: 'left',
    field: (row: SummaryFormCommentDto) => row.comment,
  },
]);

// FUNCTIONS
const fetchComments = async (request: Pagination) => {
  await summariesStore.getFormComments(props.formId, request.page, request.rowsPerPage);
};

const hasComments = computed(() => {
  const comments = formComments?.value?.get(props.formId);
  return comments?.data && comments.data.length > 0;
});
</script>
<template>
  <div class="form-summary-comments">
    <div v-if="areCommentsEnabled && hasComments" class="form-summary-comments__content">
      <SummaryTableWrapper>
        <template v-slot>
          <reorderable-table
            :rows="formComments.get(props.formId)?.data || []"
            :columns="columns"
            :loading="summariesStore.isFormCommentsLoading(props.formId)"
            itemKey="id"
            :disable-add-button="true"
            :disable-delete-button="true"
            :disableOrderable="true"
            :pagination-enabled="true"
            :pagination="formComments.get(props.formId)?.pagination"
            @request="fetchComments"
          >
            <template v-slot:body-cell-comment="props">
              <q-td :props="props">
                <BaseComment :user-response="props.row" :text="props.row.comment" />
              </q-td>
            </template>
          </reorderable-table>
        </template>
      </SummaryTableWrapper>
    </div>
    <div v-else-if="!areCommentsEnabled" class="form-summary-comments__comments-disabled">
      <div class="title">
        {{ t('form.tabs.comments') }}
      </div>
      <div class="text">
        {{ t('form.summary.status.comments-disabled') }}
      </div>
    </div>
    <div v-else-if="!hasComments" class="form-summary-comments__no-comments">
      <div class="title">
        {{ t('form.tabs.comments') }}
      </div>
      <div class="text">
        {{ t('form.summary.status.no-comments') }}
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.form-summary-comments {
  display: flex;
  flex-direction: column;
  gap: 21px;
  padding: 0 $space-md;

  &__comments-disabled,
  &__no-comments {
    color: $color-neutral-black;

    .title {
      font-weight: bold;
      font-size: $sub-header;
      margin-bottom: $space-sm;
      padding: 0 !important;
      margin: 16px 0 20px 0 !important;
    }

    .text {
      color: $color-neutral-grey-400;
      font-size: $small-text;
      padding: 0 0 $space-md 0;
    }
  }

  tbody tr:nth-child(even) {
    background-color: $color-neutral-white;
  }

  tbody tr:nth-child(odd) {
    background-color: $color-neutral-grey-200;

    td {
      border-radius: 6px;
    }
  }
}
</style>
